import { useState, useEffect } from 'react';
import axios from 'axios';
import { parseLocalDate } from '@/utils/dateUtils';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart3, Users, TrendingUp, TrendingDown, MessageSquare, 
  BookOpen, Diamond, Lightbulb, Calendar, Activity, Clock, LogIn,
  Mail, Phone, RefreshCw, Heart
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const AdminReportsPage = () => {
  const [reports, setReports] = useState(null);
  const [trends, setTrends] = useState([]);
  const [engagementReport, setEngagementReport] = useState(null);
  const [communicationsStats, setCommunicationsStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchReports();
    fetchTrends();
    fetchEngagementReport();
    fetchCommunicationsStats();
  }, []);

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/reports/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/reports/trends?days=14`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    }
  };

  const fetchEngagementReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/engagement/report?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEngagementReport(response.data);
    } catch (error) {
      console.error('Failed to fetch engagement report:', error);
    }
  };

  const fetchCommunicationsStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/engagement/communications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCommunicationsStats(response.data);
    } catch (error) {
      console.error('Failed to fetch communications stats:', error);
    }
  };

  const triggerEngagementJob = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/admin/engagement/trigger`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Engagement job triggered successfully');
      fetchCommunicationsStats();
    } catch (error) {
      toast.error('Failed to trigger engagement job');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-sand rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="admin-reports-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest flex items-center gap-3">
            <BarChart3 className="h-8 w-8" />
            Reports & Analytics
          </h1>
          <p className="text-gray-600 mt-2">
            Member engagement, activity trends, and growth insights.
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-sand/30">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="engagement">App Engagement</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Overview Stats */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Members</p>
                  <p className="text-3xl font-bold text-forest">{reports?.overview?.total_members || 0}</p>
                </div>
                <Users className="h-10 w-10 text-forest/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">New (30 days)</p>
                  <p className="text-3xl font-bold text-terracotta">{reports?.overview?.new_members_30d || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-terracotta/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active (7 days)</p>
                  <p className="text-3xl font-bold text-forest">{reports?.overview?.active_members_7d || 0}</p>
                </div>
                <Activity className="h-10 w-10 text-forest/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-base">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Growth Rate</p>
                  <p className={`text-3xl font-bold ${reports?.overview?.growth_rate >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {reports?.overview?.growth_rate >= 0 ? '+' : ''}{reports?.overview?.growth_rate || 0}%
                  </p>
                </div>
                {reports?.overview?.growth_rate >= 0 ? (
                  <TrendingUp className="h-10 w-10 text-green-600/30" />
                ) : (
                  <TrendingDown className="h-10 w-10 text-red-500/30" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Engagement Metrics */}
          <Card className="card-base lg:col-span-2">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">
                Feature Engagement (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <BookOpen className="h-8 w-8 text-forest" />
                    <div>
                      <p className="text-2xl font-bold text-forest">{reports?.engagement?.journal_entries_30d || 0}</p>
                      <p className="text-sm text-gray-500">Journal Entries</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-8 w-8 text-terracotta" />
                    <div>
                      <p className="text-2xl font-bold text-terracotta">{reports?.engagement?.comments_30d || 0}</p>
                      <p className="text-sm text-gray-500">Comments</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-8 w-8 text-purple-600" />
                    <div>
                      <p className="text-2xl font-bold text-purple-600">{reports?.engagement?.messages_30d || 0}</p>
                      <p className="text-sm text-gray-500">Messages</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-sand/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-8 w-8 text-blue-600" />
                    <div>
                      <p className="text-2xl font-bold text-blue-600">{reports?.feature_engagement?.sessions_booked || 0}</p>
                      <p className="text-sm text-gray-500">Sessions Booked</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tier Breakdown */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest">
                Members by Tier
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(reports?.tier_breakdown || {}).map(([tier, count]) => (
                  <div key={tier} className="flex items-center justify-between">
                    <span className="text-sm capitalize">
                      {tier.replace('_', ' ')}
                    </span>
                    <Badge variant="outline">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Most Active Members */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Diamond className="h-5 w-5 text-purple-600" />
                Most Active Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reports?.most_active_members?.length > 0 ? (
                <div className="space-y-3">
                  {reports.most_active_members.map((member, index) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-sand/30 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-forest">{index + 1}.</span>
                        <span className="font-medium">{member.full_name}</span>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">
                        <Diamond className="h-3 w-3 mr-1" />
                        {member.diamond_points}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No activity data yet</p>
              )}
            </CardContent>
          </Card>

          {/* AI Recommendations */}
          <Card className="card-base">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-yellow-500" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {reports?.recommendations?.length > 0 ? (
                <div className="space-y-3">
                  {reports.recommendations.map((rec, index) => (
                    <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">{rec}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Great job! No immediate recommendations. Keep up the engagement!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Trends */}
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="font-heading text-lg text-forest">
              Activity Trends (Last 14 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trends.length > 0 ? (
              <div className="overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                  {trends.map((day) => (
                    <div key={day.date} className="flex flex-col items-center w-16">
                      <div className="h-24 w-full bg-gray-100 rounded-t relative overflow-hidden">
                        <div 
                          className="absolute bottom-0 w-full bg-forest transition-all"
                          style={{ height: `${Math.min(day.new_members * 20, 100)}%` }}
                          title={`${day.new_members} new members`}
                        />
                      </div>
                      <div 
                        className="w-full bg-terracotta/30 rounded-b"
                        style={{ height: `${Math.min(day.journal_entries * 5, 30)}px` }}
                        title={`${day.journal_entries} journal entries`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {parseLocalDate(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-forest rounded" />
                    <span>New Members</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-terracotta/30 rounded" />
                    <span>Journal Entries</span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No trend data available</p>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* App Engagement Tab */}
          <TabsContent value="engagement">
            {engagementReport ? (
              <div className="space-y-6">
                {/* Engagement Overview */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Active Users (30d)</p>
                          <p className="text-3xl font-bold text-forest">{engagementReport.active_users}</p>
                        </div>
                        <Users className="h-10 w-10 text-forest/30" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Total Sessions</p>
                          <p className="text-3xl font-bold text-purple-600">{engagementReport.total_sessions}</p>
                        </div>
                        <LogIn className="h-10 w-10 text-purple-600/30" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Avg Sessions/User</p>
                          <p className="text-3xl font-bold text-terracotta">{engagementReport.avg_sessions_per_user}</p>
                        </div>
                        <Activity className="h-10 w-10 text-terracotta/30" />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="card-base">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Avg Time/User</p>
                          <p className="text-3xl font-bold text-blue-600">{Math.round(engagementReport.avg_minutes_per_user)}m</p>
                        </div>
                        <Clock className="h-10 w-10 text-blue-600/30" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  {/* Popular Pages */}
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg text-forest">
                        Most Visited Pages
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {engagementReport.popular_pages?.slice(0, 10).map((page, index) => (
                          <div key={page.page} className="flex items-center justify-between p-2 bg-sand/30 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                              <span className="text-sm font-medium truncate max-w-[200px]">{page.page}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline">{page.views} views</Badge>
                              <Badge className="bg-purple-100 text-purple-700">{page.unique_users} users</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Engagement Distribution */}
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="font-heading text-lg text-forest">
                        User Engagement Levels
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {engagementReport.engagement_distribution?.map((level) => (
                          <div key={level.level} className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span>{level.level}</span>
                              <span className="font-medium">{level.users} users</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-forest h-2 rounded-full" 
                                style={{ 
                                  width: `${Math.min((level.users / engagementReport.active_users) * 100, 100)}%` 
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Active Users Chart */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-forest">
                      Daily Active Users (Last 30 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {engagementReport.daily_active_users?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <div className="flex gap-1 min-w-max">
                          {engagementReport.daily_active_users.slice().reverse().map((day) => (
                            <div key={day.date} className="flex flex-col items-center w-8">
                              <div className="h-24 w-full bg-gray-100 rounded-t relative overflow-hidden">
                                <div 
                                  className="absolute bottom-0 w-full bg-purple-500 transition-all"
                                  style={{ height: `${Math.min(day.dau * 10, 100)}%` }}
                                  title={`${day.dau} active users`}
                                />
                              </div>
                              <p className="text-[10px] text-gray-500 mt-1 -rotate-45 origin-top-left">
                                {parseLocalDate(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">No engagement data yet. Data will appear as users interact with the app.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="card-base">
                <CardContent className="p-8 text-center">
                  <p className="text-gray-500">Loading engagement data...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Communications Tab */}
          <TabsContent value="communications">
            <div className="space-y-6">
              {/* Actions */}
              <div className="flex gap-4">
                <Button onClick={triggerEngagementJob} className="bg-forest text-white">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Trigger Engagement Job
                </Button>
              </div>

              {/* Stats */}
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="card-base">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Communications</p>
                        <p className="text-3xl font-bold text-pink-600">{communicationsStats?.total_communications || 0}</p>
                      </div>
                      <Heart className="h-10 w-10 text-pink-600/30" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-base">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Emails Sent</p>
                        <p className="text-3xl font-bold text-blue-600">{communicationsStats?.emails_sent || 0}</p>
                      </div>
                      <Mail className="h-10 w-10 text-blue-600/30" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="card-base">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">SMS Sent</p>
                        <p className="text-3xl font-bold text-green-600">{communicationsStats?.sms_sent || 0}</p>
                      </div>
                      <Phone className="h-10 w-10 text-green-600/30" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Communications */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-forest">
                    Recent Engagement Communications
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {communicationsStats?.recent?.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {communicationsStats.recent.map((comm) => (
                        <div key={comm.id} className="p-3 bg-sand/30 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className="bg-pink-100 text-pink-700">Day {comm.journey_day}</Badge>
                              <Badge variant="outline">{comm.theme?.replace('_', ' ')}</Badge>
                            </div>
                            <span className="text-xs text-gray-500">
                              {comm.sent_at ? new Date(comm.sent_at).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                              }) : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">{comm.user_email}</p>
                          <div className="flex gap-2 mt-1">
                            {comm.email_sent && <Badge className="bg-blue-100 text-blue-700 text-xs">Email</Badge>}
                            {comm.sms_sent && <Badge className="bg-green-100 text-green-700 text-xs">SMS</Badge>}
                          </div>
                          {comm.prompt_used && (
                            <p className="text-xs text-gray-500 mt-2 italic">"{comm.prompt_used.slice(0, 100)}..."</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No engagement communications sent yet. They will appear here after the daily job runs.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminReportsPage;
